import { format } from 'date-fns'
import { useBoekhouding } from './useBoekhouding'
import type { BoekhouddataBestand } from '@/types'
import { BoekhouddataBestandSchema } from '@/schemas'

export function useImportExport() {
  const { 
    transactions, 
    relaties, 
    entries, 
    splitRules, 
    accounts, 
    businessRules, 
    sequences, 
    setData,
    fileHandle 
  } = useBoekhouding()

  function getExportData(): BoekhouddataBestand {
    return {
      version: 1,
      accounts: accounts.value,
      relaties: relaties.value,
      transactions: transactions.value,
      sequences: sequences.value,
      splitRules: splitRules.value,
      businessRules: businessRules.value,
    }
  }

  function exportCSV() {
    if (entries.value.length === 0) return

    const headers = ['boekstuknr', 'datum', 'omschrijving', 'relatie', 'grootboekrekening', 'grootboeknaam', 'debet', 'credit']
    const csvContent = [
      headers.join(','),
      ...entries.value.map(e => {
        const account = accounts.value.find((a: any) => a.code === e.grootboekrekening)
        const accountName = account ? account.naam : ''
        return [
          e.boekstuknr,
          e.datum,
          `"${e.omschrijving.replace(/"/g, '""')}"`,
          `"${e.relatie.replace(/"/g, '""')}"`,
          e.grootboekrekening,
          `"${accountName.replace(/"/g, '""')}"`,
          (e.debet / 100).toFixed(2),
          (e.credit / 100).toFixed(2)
        ].join(',')
      })
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const dateStamp = format(new Date(), 'yyyy-MM-dd')
    
    link.href = url
    link.download = `boekhouding-${dateStamp}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  async function processImportContent(content: string): Promise<void> {
    const rawData = JSON.parse(content)
    
    // Validate using Zod
    const validation = BoekhouddataBestandSchema.safeParse(rawData)
    if (!validation.success) {
      console.error('Validation error:', validation.error)
      const errorMsg = validation.error.issues
        .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
        .join('\n')
      throw new Error(`Ongeldig gegevensformaat:\n${errorMsg}`)
    }
    
    const data = validation.data
    setData(data.transactions, data.splitRules, data.accounts, data.businessRules, data.relaties, data.sequences)
  }

  async function openFile() {
    const [handle] = await window.showOpenFilePicker({
      types: [
        {
          description: 'Boekhouding JSON',
          accept: { 'application/json': ['.json'] },
        },
      ],
      multiple: false,
    })

    if (handle) {
      const file = await handle.getFile()
      const content = await file.text()
      await processImportContent(content)
      fileHandle.value = handle
    }
  }

  async function saveFile() {
    if (!fileHandle.value) {
      await saveFileAs()
      return
    }

    const writable = await fileHandle.value.createWritable()
    const data = getExportData()
    await writable.write(JSON.stringify(data, null, 2))
    await writable.close()
  }

  async function createNewFile() {
    const dateStamp = format(new Date(), 'yyyy-MM-dd')
    const handle = await window.showSaveFilePicker({
      suggestedName: `boekhouding-${dateStamp}.json`,
      types: [{ description: 'Boekhouding JSON', accept: { 'application/json': ['.json'] } }],
    })
    if (handle) {
      setData([], [])
      fileHandle.value = handle
      await saveFile()
    }
  }

  async function saveFileAs() {
    const dateStamp = format(new Date(), 'yyyy-MM-dd')
    const handle = await window.showSaveFilePicker({
      suggestedName: `boekhouding-${dateStamp}.json`,
      types: [
        {
          description: 'Boekhouding JSON',
          accept: { 'application/json': ['.json'] },
        },
      ],
    })

    if (handle) {
      fileHandle.value = handle
      await saveFile()
    }
  }

  return {
    exportCSV,
    openFile,
    createNewFile,
    saveFile,
    saveFileAs,
    fileHandle
  }
}
